import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  LayoutDashboard, Bell, GraduationCap, BarChart3, Mail, Calendar, FileText,
  Users, Settings, TrendingUp, TrendingDown, ClipboardCheck, AlertCircle,
} from "lucide-react";

/* ---------- Small primitives ---------- */

export const HealthDot = ({ tone = "good" }) => {
  const c = { good: "#22C55E", warn: "#F5B93C", bad: "#F87171" }[tone];
  return <span className="inline-block h-2 w-2 rounded-full" style={{ background: c }} />;
};

export const ProgressBar = ({ value, tone = "good" }) => {
  const c = { good: "#22C55E", warn: "#F5B93C", bad: "#F87171" }[tone];
  return (
    <div className="h-1.5 w-full rounded-full bg-[#EEF0F3] overflow-hidden">
      <motion.div className="h-full rounded-full" style={{ background: c }}
        initial={{ width: 0 }} whileInView={{ width: `${value}%` }} viewport={{ once: true }}
        transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }} />
    </div>
  );
};

export const KpiCard = ({ icon: Icon, label, value, sub, trend, tone = "good" }) => (
  <div className="bg-white rounded-2xl border border-[#ECECE8] p-4 shadow-[0_2px_10px_-4px_rgba(16,17,23,0.05)]">
    <div className="flex items-center gap-2.5">
      <span className="h-8 w-8 rounded-lg bg-[#EEF0FB] flex items-center justify-center shrink-0">
        <Icon size={15} className="text-[#6366F1]" />
      </span>
      <span className="text-[11px] font-medium text-[#6B7280] leading-tight">{label}</span>
    </div>
    <div className="mt-3 font-mono-accent text-[26px] leading-none font-semibold text-[#1A1D26]">{value}</div>
    {sub && (
      <div className="mt-2 flex items-center gap-1 text-[10.5px] text-[#6B7280]">
        {trend === "up" && <TrendingUp size={12} className="text-[#22C55E]" />}
        {trend === "down" && <TrendingDown size={12} className="text-[#F87171]" />}
        <span className={trend === "up" ? "text-[#22C55E]" : trend === "down" ? "text-[#F87171]" : ""}>{sub}</span>
      </div>
    )}
  </div>
);

/* ---------- AHI radial dot visualization ---------- */

export const AhiRadial = ({ score = "85.6", size = 150 }) => {
  const cx = size / 2, cy = size / 2;
  const rings = [
    { r: size * 0.20, n: 10 }, { r: size * 0.28, n: 16 }, { r: size * 0.36, n: 22 },
    { r: size * 0.44, n: 28 }, { r: size * 0.50, n: 34 },
  ];
  const dots = [];
  rings.forEach((ring, ri) => {
    for (let i = 0; i < ring.n; i++) {
      const a = (i / ring.n) * Math.PI * 2 + ri * 0.35;
      const x = cx + Math.cos(a) * ring.r;
      const y = cy + Math.sin(a) * ring.r;
      const roll = (i * 7 + ri * 3) % 10;
      const tone = roll < 7 ? "#22C55E" : roll < 9 ? "#F5B93C" : "#F87171";
      dots.push({ x, y, tone, d: ri * 0.05 + i * 0.006 });
    }
  });
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="overflow-visible">
      {dots.map((d, i) => (
        <motion.circle key={i} cx={d.x} cy={d.y} r={size * 0.014} fill={d.tone}
          initial={{ opacity: 0, scale: 0 }} whileInView={{ opacity: 0.9, scale: 1 }}
          viewport={{ once: true }} transition={{ duration: 0.4, delay: d.d }} />
      ))}
      <circle cx={cx} cy={cy} r={size * 0.11} fill="#22C55E" opacity="0.18" />
      <circle cx={cx} cy={cy} r={size * 0.07} fill="#22C55E" />
      <circle cx={cx} cy={cy} r={size * 0.035} fill="#0E1B14" />
    </svg>
  );
};

/* ---------- Mini charts (SVG) ---------- */

export const MiniArea = ({ color = "#5B7CFA", h = 90 }) => {
  const pts = [30, 42, 38, 55, 48, 62, 58, 70, 66, 78];
  const w = 260;
  const step = w / (pts.length - 1);
  const max = 90;
  const coords = pts.map((p, i) => [i * step, h - (p / max) * h]);
  const line = coords.map((c, i) => `${i === 0 ? "M" : "L"}${c[0]},${c[1]}`).join(" ");
  const area = `${line} L${w},${h} L0,${h} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full" preserveAspectRatio="none" style={{ height: h }}>
      <defs>
        <linearGradient id={`ag-${color}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.28" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <motion.path d={area} fill={`url(#ag-${color})`}
        initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.8 }} />
      <motion.path d={line} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round"
        initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }} transition={{ duration: 1.2, ease: "easeInOut" }} />
    </svg>
  );
};

export const MiniBars = ({ data = [75, 85, 94, 85, 75, 94, 85, 75, 94] }) => {
  const w = 260, h = 96, gap = 8;
  const bw = (w - gap * (data.length - 1)) / data.length;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full" style={{ height: h }}>
      <defs>
        <linearGradient id="bar-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#4F46E5" />
          <stop offset="100%" stopColor="#A5B4FC" />
        </linearGradient>
      </defs>
      {data.map((d, i) => {
        const bh = (d / 100) * (h - 6);
        return (
          <motion.rect key={i} x={i * (bw + gap)} width={bw} rx="3" fill="url(#bar-grad)"
            initial={{ height: 0, y: h }} whileInView={{ height: bh, y: h - bh }} viewport={{ once: true }}
            transition={{ duration: 0.7, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }} />
        );
      })}
    </svg>
  );
};

/* ---------- Command Center dashboard mockup ---------- */

const navItems = [
  { icon: LayoutDashboard, label: "Dashboard", active: true },
  { icon: Bell, label: "Notifications", badge: "4" },
  { icon: GraduationCap, label: "Classes" },
  { icon: BarChart3, label: "Analytics" },
  { icon: Mail, label: "PCC" },
  { icon: Calendar, label: "Calendar" },
  { icon: FileText, label: "Exam" },
  { icon: Users, label: "User Management" },
  { icon: Settings, label: "Setting" },
];

const kpis = [
  { icon: ClipboardCheck, label: "Classes Running", value: "42/50", sub: "6 late starts detected" },
  { icon: Users, label: "Attendance", value: "94.2%", sub: "+2.1% vs yesterday", trend: "up" },
  { icon: FileText, label: "EOC Compliance", value: "71%", sub: "17 EOCs pending today" },
  { icon: Mail, label: "PCC Escalations", value: "3", sub: "Unresolved items" },
];

const ahiBars = [
  { label: "Attendance Discipline", v: 92, t: "good" },
  { label: "Lesson Execution & EOC", v: 89, t: "good" },
  { label: "Homework Consistency", v: 78, t: "warn" },
  { label: "Behaviour Stability", v: 85, t: "good" },
];

const tasks = [
  { title: "Approve Term-1 Exam Result", meta: "Today, by 1:30 PM", tone: "warn" },
  { title: "Weekly Lesson Log Review", meta: "Today, by 1:30 PM", tone: "good" },
  { title: "Attendance Dip in Class 8-C", meta: "-15% this week", tone: "bad" },
];

export const CommandCenterMockup = ({ compact = false, live = false }) => {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!live) return;
    const id = setInterval(() => setTick((t) => t + 1), 2600);
    return () => clearInterval(id);
  }, [live]);
  const kpisLive = live
    ? kpis.map((k, i) =>
        i === 0 ? { ...k, value: ["42/50", "44/50", "43/50"][tick % 3], sub: ["6 late starts detected", "3 late starts detected", "5 late starts detected"][tick % 3] }
        : i === 1 ? { ...k, value: ["94.2%", "94.6%", "95.1%"][tick % 3], sub: "+2.1% vs yesterday", trend: "up" }
        : k)
    : kpis;
  return (
  <div className="rounded-2xl overflow-hidden border border-[#ECECE8] bg-white shadow-[0_30px_80px_-30px_rgba(16,17,23,0.35)]"
    data-testid="command-center-mockup">
    {/* window bar */}
    <div className="h-8 bg-[#0E1117] flex items-center gap-1.5 px-3.5 border-b border-white/5">
      <span className="h-2.5 w-2.5 rounded-full bg-[#F87171]" />
      <span className="h-2.5 w-2.5 rounded-full bg-[#F5B93C]" />
      <span className="h-2.5 w-2.5 rounded-full bg-[#22C55E]" />
      <span className="ml-3 text-[10px] text-white/40 font-mono-accent">partecon · principal command center</span>
      {live && (
        <span className="ml-auto flex items-center gap-1.5 text-[9px] font-mono-accent text-[#22C55E]">
          <motion.span className="h-1.5 w-1.5 rounded-full bg-[#22C55E]" animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1.6, repeat: Infinity }} /> LIVE
        </span>
      )}
    </div>
    <div className="flex">
      {/* Sidebar */}
      <div className={`bg-[#0E1117] py-4 px-3 ${compact ? "hidden sm:block w-44" : "w-48"} shrink-0`}>
        <div className="flex items-center gap-2 px-1 pb-4 mb-2 border-b border-white/5">
          <span className="h-6 w-6 rounded-md bg-[#6366F1] flex items-center justify-center text-white text-[11px] font-bold font-display">P</span>
          <span className="text-white text-[12px] font-medium truncate">PARTECON Smart School</span>
        </div>
        {navItems.map((n) => (
          <div key={n.label}
            className={`flex items-center gap-2.5 rounded-lg px-2.5 py-[7px] mb-0.5 text-[11.5px] ${
              n.active ? "bg-white/10 text-white" : "text-white/45"}`}>
            <n.icon size={14} />
            <span className="truncate">{n.label}</span>
            {n.badge && <span className="ml-auto h-4 w-4 rounded-full bg-[#F87171] text-white text-[9px] flex items-center justify-center">{n.badge}</span>}
          </div>
        ))}
      </div>

      {/* Canvas */}
      <div className="flex-1 bg-[#F6F5F1] p-4 min-w-0">
        <div className="flex items-center gap-2.5 mb-3">
          <span className="h-7 w-7 rounded-lg bg-[#EEF0FB] flex items-center justify-center">
            <LayoutDashboard size={14} className="text-[#6366F1]" />
          </span>
          <div>
            <div className="text-[13px] font-semibold text-[#1A1D26] font-display leading-none">Command Center</div>
            <div className="text-[9.5px] text-[#6B7280] mt-1">Key metrics & tasks requiring attention</div>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
          {kpisLive.map((k) => <KpiCard key={k.label} {...k} />)}
        </div>

        <div className="grid lg:grid-cols-[1.3fr_1fr] gap-2.5 mt-2.5">
          {/* AHI card */}
          <div className="bg-white rounded-2xl border border-[#ECECE8] p-4">
            <div className="text-[11px] font-semibold text-[#1A1D26] mb-3">Analytical Health Index (AHI)</div>
            <div className="flex items-center gap-4">
              <div className="rounded-xl bg-[#0E1B2A] p-2 flex items-center gap-2 shrink-0">
                <div>
                  <div className="text-[9px] text-white/60 leading-none">AHI Score</div>
                  <div className="font-mono-accent text-2xl font-semibold text-white leading-none mt-1">85.6</div>
                  <div className="flex items-center gap-1 mt-1.5 text-[8.5px] text-[#22C55E]">
                    <TrendingUp size={9} /> Good Health
                  </div>
                </div>
                <AhiRadial size={92} />
              </div>
              <div className="flex-1 space-y-2 min-w-0">
                {ahiBars.map((b) => (
                  <div key={b.label}>
                    <div className="flex justify-between text-[9.5px] mb-1">
                      <span className="text-[#6B7280] truncate pr-2">{b.label}</span>
                      <span className="text-[#1A1D26] font-medium font-mono-accent">{b.v}%</span>
                    </div>
                    <ProgressBar value={b.v} tone={b.t} />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Tasks */}
          <div className="bg-white rounded-2xl border border-[#ECECE8] p-4">
            <div className="text-[11px] font-semibold text-[#1A1D26] mb-2.5">Tasks to Review</div>
            <div className="space-y-2.5">
              {tasks.map((t) => (
                <div key={t.title} className="flex items-start gap-2 pb-2.5 border-b border-[#F0F0EC] last:border-0">
                  <span className="mt-1"><HealthDot tone={t.tone} /></span>
                  <div className="min-w-0">
                    <div className="text-[10.5px] font-medium text-[#1A1D26] leading-snug">{t.title}</div>
                    <div className="text-[9px] text-[#6B7280] mt-0.5">{t.meta}</div>
                  </div>
                  <AlertCircle size={11} className="text-[#C7C7C0] ml-auto shrink-0 mt-0.5" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  );
};

/* ---------- PCC inbox mockup ---------- */

const pccRows = [
  { name: "District Education Officer", tag: "Government", urgent: true, subject: "Circular 102: Compliance", init: "DE", tone: "#E0E7FF" },
  { name: "Mr. Sharma", tag: "Parent", urgent: true, subject: "Complaint regarding Class 8-A", init: "MS", tone: "#FCE9EA" },
  { name: "Anil Sharma", tag: "Teacher", urgent: false, subject: "Resource request for 2nd Term", init: "AS", tone: "#DCFCE7" },
  { name: "Ms. Priya Verma", tag: "Teacher", urgent: false, subject: "Leave Application", init: "PV", tone: "#EEF0FB" },
];

export const PccMockup = () => (
  <div className="rounded-2xl overflow-hidden border border-[#ECECE8] bg-white shadow-[0_24px_70px_-28px_rgba(16,17,23,0.28)]" data-testid="pcc-mockup">
    <div className="bg-white p-4 border-b border-[#ECECE8] flex items-center gap-2.5">
      <span className="h-8 w-8 rounded-lg bg-[#EEF0FB] flex items-center justify-center">
        <Mail size={15} className="text-[#6366F1]" />
      </span>
      <div>
        <div className="text-[13px] font-semibold text-[#1A1D26] font-display leading-none">PARTECON Communication Channel</div>
        <div className="text-[10px] text-[#6B7280] mt-1">Secure, role-based communication for schools</div>
      </div>
      <span className="ml-auto badge-soft text-[10px] px-3 py-1.5">Compose New</span>
    </div>
    <div className="p-2">
      {pccRows.map((r) => (
        <div key={r.name} className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-[#F6F5F1] transition-colors">
          <span className="h-8 w-8 rounded-full flex items-center justify-center text-[10px] font-semibold text-[#1A1D26] shrink-0" style={{ background: r.tone }}>{r.init}</span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-[12px] font-medium text-[#1A1D26] truncate">{r.name}</span>
              <span className="badge-soft text-[9px] px-1.5 py-0.5">{r.tag}</span>
              {r.urgent && <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#FCE9EA] text-[#DC2626] font-semibold">Urgent</span>}
            </div>
            <div className="text-[11px] text-[#6B7280] truncate mt-0.5">{r.subject}</div>
          </div>
          <span className="h-2 w-2 rounded-full bg-[#6366F1] shrink-0" />
        </div>
      ))}
    </div>
  </div>
);
